"use client";

import React from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Quiz02Icon,
  StopWatchIcon,
  BrainCircuitIcon,
  BulbChargingIcon,
  Analytics01Icon,
  Calendar03Icon,
  BookOpen01Icon,
  FireIcon,
  Notebook01Icon,
  ChampionIcon,
  Mortarboard02Icon,
  Search01Icon,
} from "@hugeicons/core-free-icons";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { ClosingCta } from "@/components/landing/ClosingCta";
import { useBoard } from "@/components/BoardProvider";

/**
 * All-features bento. Cards share one chassis; the larger tiles carry a small
 * CSS-only product vignette (option rows, timer, confidence bars…) — one
 * accent colour, neutrals for everything else.
 */

function IconChip({ icon }: { icon: typeof Quiz02Icon }) {
  return (
    <span className="inline-flex w-fit rounded-lg bg-primary/10 p-2.5 text-primary">
      <HugeiconsIcon icon={icon} size={22} strokeWidth={1.8} />
    </span>
  );
}

function Card({
  icon,
  title,
  blurb,
  className = "",
  children,
}: {
  icon: typeof Quiz02Icon;
  title: string;
  blurb: string;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={`flex flex-col rounded-2xl border border-border/60 bg-card p-6 transition-shadow duration-200 hover:shadow-md ${className}`}
    >
      <IconChip icon={icon} />
      <h2 className="mt-4 font-polysans text-lg font-semibold">{title}</h2>
      <p className="mt-1.5 font-polysans text-sm leading-relaxed text-muted-foreground">
        {blurb}
      </p>
      {children ? <div className="mt-auto pt-6">{children}</div> : null}
    </div>
  );
}

/* ---------- vignettes (pure CSS, accent + neutrals only) ---------- */

function OptionRowsVignette() {
  const rows = [
    { letter: "A", selected: false, w: "w-3/4" },
    { letter: "B", selected: true, w: "w-2/3" },
    { letter: "C", selected: false, w: "w-4/5" },
  ];
  return (
    <div className="space-y-2" aria-hidden>
      {rows.map((r) => (
        <div
          key={r.letter}
          className={`flex items-center gap-3 rounded-xl border-2 p-3 ${
            r.selected ? "border-primary bg-primary/5" : "border-border"
          }`}
        >
          <span
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 text-xs font-bold ${
              r.selected
                ? "border-primary bg-primary text-primary-foreground"
                : "border-muted-foreground/50 text-muted-foreground"
            }`}
          >
            {r.letter}
          </span>
          <span className={`h-2 ${r.w} rounded-full bg-muted`} />
        </div>
      ))}
    </div>
  );
}

function TimerVignette() {
  return (
    <div aria-hidden>
      <div className="font-polysans text-4xl font-semibold tabular-nums">75:00</div>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
        <div className="h-full w-2/3 rounded-full bg-primary" />
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Paper 1 · 20 questions</p>
    </div>
  );
}

function BarsVignette() {
  const bars = [
    { h: "40%", o: 0.3 },
    { h: "65%", o: 0.5 },
    { h: "30%", o: 0.25 },
    { h: "80%", o: 1 },
    { h: "55%", o: 0.45 },
    { h: "70%", o: 0.7 },
  ];
  return (
    <div aria-hidden>
      <div className="flex h-20 items-end gap-2">
        {bars.map((b, i) => (
          <div
            key={i}
            className="flex-1 rounded-t-md bg-primary"
            style={{ height: b.h, opacity: b.o }}
          />
        ))}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Confidence by topic</p>
    </div>
  );
}

function StreakVignette() {
  return (
    <div aria-hidden>
      <div className="flex items-center gap-2">
        {Array.from({ length: 7 }, (_, i) => (
          <span
            key={i}
            className={`h-3.5 w-3.5 rounded-full ${
              i < 5 ? "bg-primary" : "border-2 border-border"
            }`}
          />
        ))}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">5-day streak</p>
    </div>
  );
}

function DojoVignette() {
  return (
    <div aria-hidden>
      <div className="font-mono text-2xl">
        17 × 24 = <span className="text-primary">?</span>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">4.2s average this run</p>
    </div>
  );
}

function SearchVignette() {
  return (
    <div
      aria-hidden
      className="flex items-center gap-2.5 rounded-xl border border-border bg-background px-4 py-3 text-muted-foreground"
    >
      <HugeiconsIcon icon={Search01Icon} size={16} strokeWidth={2} />
      <span className="truncate font-polysans text-sm">
        questions where the discriminant is negative…
      </span>
    </div>
  );
}

export default function FeaturesPage() {
  const board = useBoard();
  const s = board.shortName;

  return (
    <main className="min-h-screen bg-background text-foreground">
      <Header />

      <section className="mx-auto max-w-6xl px-6 pt-16 md:pt-24">
        <h1 className="text-center text-5xl md:text-6xl font-script text-foreground">
          All features
        </h1>
        <p className="mt-4 text-center text-muted-foreground max-w-2xl mx-auto font-polysans text-xl md:text-2xl">
          Every tool in your {s} prep, and what it does for your score.
        </p>

        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-6">
          <Card
            icon={Quiz02Icon}
            title="Question Bank"
            blurb={`Every real past ${s} question, organised by topic. Pick a weak area and start clearing it.`}
            className="sm:col-span-2 lg:col-span-4"
          >
            <OptionRowsVignette />
          </Card>

          <Card
            icon={StopWatchIcon}
            title="Timed Mock Exams"
            blurb="Sit real papers under timed conditions in an exam-accurate interface."
            className="lg:col-span-2"
          >
            <TimerVignette />
          </Card>

          <Card
            icon={Analytics01Icon}
            title="Progress Analytics"
            blurb="Confidence tracked topic by topic, so you fix weak spots before the exam finds them."
            className="lg:col-span-2"
          >
            <BarsVignette />
          </Card>

          <Card
            icon={Calendar03Icon}
            title="Question of the Day"
            blurb="One sharp question a day keeps the streak alive and the rust off."
            className="lg:col-span-2"
          >
            <StreakVignette />
          </Card>

          <Card
            icon={FireIcon}
            title="Maths Dojo"
            blurb="Fast mental-maths drills that build the speed the exam rewards."
            className="lg:col-span-2"
          >
            <DojoVignette />
          </Card>

          <Card
            icon={BrainCircuitIcon}
            title="Adaptive Practice"
            blurb="Auto-generated packs that target your weakest topics and evolve as you improve."
            className="lg:col-span-2"
          />

          <Card
            icon={BulbChargingIcon}
            title="Solutions & Hints"
            blurb="Every question fully worked, with staged hints for when you're stuck."
            className="lg:col-span-2"
          />

          <Card
            icon={BookOpen01Icon}
            title="In-Depth Textbook"
            blurb="Concise, exam-focused notes for every specification point."
            className="lg:col-span-2"
          />

          <Card
            icon={Notebook01Icon}
            title="Revision Journal"
            blurb="Log every session, track your mistakes, and never repeat them."
            className="lg:col-span-2"
          />

          <Card
            icon={ChampionIcon}
            title="Leaderboard & Friends"
            blurb="Compete with friends chasing the same offer."
            className="lg:col-span-2"
          />

          <Card
            icon={Mortarboard02Icon}
            title="Mentors"
            blurb={`Guidance from students who sat the ${s} and got the offer.`}
            className="lg:col-span-2"
          />

          <Card
            icon={Search01Icon}
            title="Smart Search"
            blurb="Find any question by the concept it tests, not just keywords."
            className="sm:col-span-2 lg:col-span-4"
          >
            <SearchVignette />
          </Card>
        </div>
      </section>

      <ClosingCta />
      <Footer />
    </main>
  );
}
